import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function CatalogueSection({ pets, onAddToCart, whatsappUrl }) {
  if (!pets.length) {
    return (
      <section className="catalogue" id="catalogue">
        <div className="catalogue-inner">
          <motion.h2 initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            Catalogue
          </motion.h2>
          <div className="catalogue-empty" role="status" aria-label="Catalogue empty">
            <div className="catalogue-empty-title">No pups listed yet</div>
            <div className="catalogue-empty-sub">
              New pups are added regularly. Message us on WhatsApp and we’ll share the latest availability.
            </div>
            <div className="catalogue-empty-actions">
              {whatsappUrl ? (
                <a className="catalogue-empty-primary" href={whatsappUrl} target="_blank" rel="noreferrer">
                  Message on WhatsApp
                </a>
              ) : null}
              <Link className="catalogue-empty-secondary" to="/contact">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="catalogue" id="catalogue">
      <div className="catalogue-inner">
        <div className="adoption-process" aria-label="Adoption process">
          <div className="adoption-process-head">
            <div className="adoption-process-kicker">Simple adoption process</div>
            <h3>How it works</h3>
            <p>Choose a pup, send an enquiry on WhatsApp, and we’ll guide you through pickup or shipping.</p>
          </div>
          <div className="adoption-steps">
            {[
              { title: 'Browse & pick', body: 'View available pups and choose your favorite.', icon: '🐾' },
              { title: 'Send enquiry', body: 'Add to enquiry and send details to WhatsApp.', icon: '💬' },
              { title: 'Confirm availability', body: 'We confirm status and answer all questions.', icon: '✅' },
              { title: 'Pickup or shipping', body: 'We coordinate the smoothest handoff for your home.', icon: '🚚' }
            ].map((s) => (
              <div className="adoption-step" key={s.title}>
                <div className="adoption-step-icon" aria-hidden="true">
                  {s.icon}
                </div>
                <div className="adoption-step-title">{s.title}</div>
                <div className="adoption-step-body">{s.body}</div>
              </div>
            ))}
          </div>
        </div>

        <motion.h2 initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          Available Pups
        </motion.h2>

        <div className="catalogue-grid" aria-label="Pup catalogue">
          {pets.map((pet, idx) => {
            const s = String(pet.status || '').trim().toLowerCase();
            const isUnavailable = s === 'sold' || s === 'not available' || s === 'unavailable' || s === 'out of stock';
            const statusLabel = isUnavailable ? 'Not available' : pet.status || 'Available';
            return (
              <motion.div
                key={pet.id}
                className={`catalogue-card ${isUnavailable ? 'is-sold' : ''}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (idx % 6) * 0.03 }}
                whileHover={isUnavailable ? {} : { y: -6 }}
              >
                <Link className="catalogue-link" to={`/pup/${pet.id}`} aria-label={`View details for ${pet.title}`}>
                  <div className="catalogue-photo">
                    {pet.images?.[0] ? (
                      <img src={pet.images[0]} alt={pet.title} loading="lazy" />
                    ) : (
                      <div className="photo-placeholder" aria-hidden="true" />
                    )}
                    <div className="catalogue-badges" aria-hidden="true">
                      <span className={`status-badge ${isUnavailable ? 'is-off' : 'is-on'}`}>{statusLabel}</span>
                    </div>
                  </div>
                  <div className="catalogue-name">
                    {pet.title} - {pet.breed ? `${pet.breed} ` : ''}
                    {pet.sex ? `${pet.sex} ` : ''}({statusLabel})
                  </div>
                </Link>
                <div className="catalogue-price">
                  {typeof pet.price === 'number'
                    ? `Adoption fee: $${pet.price.toFixed(2)}`
                    : 'Ask about adoption fee'}
                </div>
                {!isUnavailable ? (
                  <button className="catalogue-cta" type="button" onClick={onAddToCart(pet.id)}>
                    Add to enquiry
                  </button>
                ) : null}
                <Link className="catalogue-details" to={`/pup/${pet.id}`}>
                  View details
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
